import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, MapPin, Compass, Star, TrendingUp } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import RotatingEarth, { SurfSpot } from '@/components/RotatingEarth';
import heroImage from '@/assets/hero-drone-surf.jpg';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDestinations } from '@/hooks/useDestinations';


// News articles data
const newsArticles = [
  {
    id: 1,
    title: "El Niño Surf Travel: Where to Go Now",
    excerpt: "The best surf destinations to book while the swell window is open.",
    date: "Jan 15, 2026",
    category: "Forecast",
    readTime: "4 min read"
  },
  {
    id: 2,
    title: "Portugal's Quiet Surf Destinations",
    excerpt: "Beyond the crowds: surf camps, beginner spots, and surf accommodation along the Silver Coast.",
    date: "Jan 12, 2026",
    category: "Destinations",
    readTime: "6 min read"
  },
  {
    id: 3,
    title: "Sustainable Surf Travel",
    excerpt: "Chase better waves with a lighter footprint — from surf accommodation to packing smart.",
    date: "Jan 10, 2026",
    category: "Travel Tips",
    readTime: "5 min read"
  }
];

// Editor's top picks — Summer season (Jun–Aug, Northern Hemisphere)
// Slugs are hardcoded; the name, country, image and summary are pulled from Supabase.
const SEASON_LABEL = 'Summer 2026';
const editorsPickSlugs: { slug: string; highlight: string }[] = [
  { slug: 'uluwatu', highlight: 'Peak Season' },
  { slug: 'jeffreys-bay', highlight: 'Pro Swell Window' },
  { slug: 'hossegor', highlight: 'European Summer' },
];

const Index = () => {
  const navigate = useNavigate();
  const { data: allDestinations } = useDestinations();
  const editorsPicks = editorsPickSlugs
    .map(({ slug, highlight }) => {
      const dest = allDestinations?.find((d) => d.id === slug);
      return dest ? { ...dest, highlight } : null;
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  // Build globe pins from live destinations (only those with coordinates)
  const surfSpots: SurfSpot[] = (allDestinations ?? [])
    .filter((d) => d.lat != null && d.lng != null)
    .map((d) => ({
      id: d.id,
      name: d.name,
      lat: d.lat as number,
      lng: d.lng as number,
      country: d.country,
      summary: d.description || '',
      image: d.image,
      skillLevel: d.skillLevel,
      skillLabel: d.skillLabel,
      waveType: d.waveType,
      crowdLevel: d.crowdLevel,
      bestMonths: d.bestMonths,
      costRating: d.costRating,
      consistency: d.consistency,
      vibe: d.vibeTags,
    }));

  const [globeSize, setGlobeSize] = useState({ width: 1200, height: 700 });

  useEffect(() => {
    const updateSize = () => {
      const vw = window.innerWidth;
      const isMobile = vw < 768;
      // Leave breathing room so the globe + its zoom-control column never clip
      // the viewport edge at common desktop widths (1280–1600px).
      const w = isMobile ? vw - 24 : Math.min(vw - 96, 1280);
      const h = isMobile ? w * 1.05 : Math.min(760, w * 0.5);
      setGlobeSize({ width: w, height: h });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleSpotClick = (spot: SurfSpot) => {
    navigate(`/destinations/${spot.id}`);
  };

  return (
    <Layout>
      <Seo
        title="Swell Breaks | Find the Surf Destination That Fits You"
        description="Compare surf destinations by season, ability, budget, crowds, wave type and travel style. Find the surf trip that fits what you actually want."
        path="/"
      />
      {/* Fixed Background Image for entire page */}
      <div className="fixed inset-0 -z-10">
        <img
          src={heroImage}
          alt="Ocean waves"
          className="w-full h-full object-cover"
          // This is the homepage's LCP element. fetchPriority hints the
          // browser to prioritize it over same-priority requests (fonts,
          // analytics) once discovered -- doesn't change when discovery
          // happens (still gated behind JS render, since there's no SSR),
          // but does help once it does.
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-background/40 to-background/80" />
      </div>

      {/* Hero Section - Compact on desktop */}
      <section className="relative min-h-[90vh] md:min-h-[70vh] lg:min-h-[65vh] flex items-center justify-center overflow-hidden px-4">
        {/* Content */}
        <div className="relative z-10 container mx-auto text-center">
          <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
            {/* Title */}
            <h1 className="font-serif text-3xl sm:text-5xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-[1.1]">
              Find the Surf Destination
              <span className="block text-primary mt-1 md:mt-2">Built for You</span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm md:text-lg text-foreground/70 max-w-xl mx-auto leading-relaxed px-4 md:px-0">
              A surf travel platform that matches surfers with the right surf destinations, surf trips, and surf accommodation — by skill, budget, and season.
            </p>

            {/* CTA - Compact button */}
            <div className="pt-2 md:pt-4">
              <Link to="/trip-planner">
                <Button size="lg" className="gap-2 text-sm md:text-base px-6 md:px-8 py-5 md:py-5 rounded-full shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Plan Your Surf Trip
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator - hide on mobile */}
        <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <div className="w-6 h-10 rounded-full border-2 border-foreground/30 flex justify-center pt-2">
            <div className="w-1 h-2.5 bg-foreground/40 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Interactive Globe Section - Compact. Mobile padding/margins here
          (py-8, mb-4, mt-4 below) are deliberately tighter than md:+ -- the
          globe's own size is untouched, but the surrounding chrome is
          trimmed so the new compact mobile preview sits close enough to
          the globe to read as connected to it, not a separate block
          requiring its own scroll. */}
      <section className="py-8 md:py-16 lg:py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4 md:mb-10">
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 md:mb-4">
              Explore the Best Surf Destinations
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
              Spin the globe. The closest card is your match — tap it to plan your surf trip.
            </p>
          </div>

          {/* The globe canvas is deliberately narrower than the available
              width (RotatingEarth reserves a right-hand column so the zoom
              buttons never overlap the sphere), so the sphere itself -- not
              just this wrapper -- is already centred within the canvas. The
              only thing that needs centring here is that narrower canvas
              within the full section width. `justify-center` below is what
              does that; it was present but inert as long as its single
              child was `w-full`, which stretched to consume the entire row
              and left nothing to center. Removing that -- not a margin or
              translateX offset -- is the actual fix: the composite now sizes
              to its own content (globe + zoom-control column) and the flex
              container centers it, regardless of where the zoom controls
              land inside it. */}
          <div className="flex justify-center">
            <div className="relative">
              <RotatingEarth
                width={globeSize.width}
                height={globeSize.height}
                surfSpots={surfSpots}
                onSpotClick={handleSpotClick}
                className="rounded-2xl shadow-2xl mx-auto"
              />
              {/* Glow effect behind globe */}
              <div className="absolute inset-0 -z-10 blur-[100px] opacity-20 bg-primary rounded-full scale-75" />
            </div>
          </div>

          <div className="text-center mt-4 md:mt-8">
            <Link to="/destinations">
              <Button variant="outline" size="default" className="gap-2 rounded-full backdrop-blur-sm bg-background border-foreground/25 px-5 md:px-6 py-4 md:py-5 text-sm md:text-base hover:-translate-y-1 transition-all">
                <MapPin className="h-4 w-4" />
                Browse All Destinations
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Editor's Top Picks Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 mb-3 md:mb-4">
              <Star className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">Top Picks · {SEASON_LABEL}</span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 md:mb-3">
              Top Surf Destinations This Season
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
              Three standout surf trips for the {SEASON_LABEL} window — from surf holidays to surf camps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {editorsPicks.map((pick) => (
              <Link key={pick.id} to={`/destinations/${pick.id}`}>
                <Card className="group overflow-hidden bg-card backdrop-blur-sm border-foreground/20 hover:border-primary/40 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer h-full">
                  <div className="relative h-32 md:h-36 overflow-hidden">
                    <img 
                      src={pick.image} 
                      alt={pick.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                    <Badge className="absolute top-2 left-2 md:top-3 md:left-3 bg-primary text-primary-foreground text-xs">
                      {pick.highlight}
                    </Badge>
                  </div>
                  <CardContent className="p-3 md:p-4">
                    <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1.5">
                      <MapPin className="h-3 w-3" />
                      {pick.country}
                    </div>
                    <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground mb-1.5 group-hover:text-primary transition-colors">
                      {pick.name}
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed line-clamp-2">
                      {pick.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 mb-3 md:mb-4">
              <TrendingUp className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">Latest Updates</span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2 md:mb-3">
              Surf Travel Insights
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
              Forecasts, surf travel guides, and trip tips to help you plan your next surf trip.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {newsArticles.map((article) => (
              <Card key={article.id} className="bg-card backdrop-blur-sm border-dashed border-foreground/25">
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 mb-2 md:mb-3">
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-0 text-xs">
                      {article.category}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                    <span className="text-xs text-muted-foreground">{article.readTime}</span>
                  </div>
                  <h3 className="font-serif text-base md:text-lg font-semibold text-foreground mb-1.5 md:mb-2 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-muted-foreground text-xs md:text-sm leading-relaxed mb-2 md:mb-3 line-clamp-2">
                    {article.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-6 md:mt-8">
            <Link to="/media">
              <Button variant="outline" size="default" className="gap-2 rounded-full backdrop-blur-sm bg-background border-foreground/25 px-5 md:px-6 py-4 md:py-5 text-sm md:text-base hover:-translate-y-1 transition-all">
                Read More Articles
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>


      {/* Spacer for footer */}
      <div className="h-12" />
    </Layout>
  );
};

export default Index;
